import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, FileText, Clock, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const statusMap = {
  pending: { label: "بانتظار التأكيد", color: "#F5A623", icon: Clock },
  approved: { label: "تم القبض ✅", color: "#7ED321", icon: CheckCircle },
  rejected: { label: "مرفوض", color: "#E85D45", icon: XCircle },
};

const Debts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/me/debts`);
      setData(data);
    } catch {
      toast.error("فشل تحميل بيانات الدين");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    fetchData();
  }, [user, navigate]);

  const handlePay = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("الرجاء إدخال مبلغ صحيح");
      return;
    }
    if (amt > (data?.total_debt || 0)) {
      toast.error("المبلغ أكبر من الدين");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/me/pay-debt`, { amount: amt, note });
      toast.success("تم إرسال طلب السداد، سيتم الاتصال بك لتأكيد القبض");
      setShowForm(false);
      setAmount("");
      setNote("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل إرسال الطلب");
    } finally {
      setSubmitting(false);
    }
  };

  const hasPending = data?.pending_payments?.length > 0;

  if (loading) {
    return <div className="min-h-screen bg-transparent flex items-center justify-center"><p>جاري التحميل...</p></div>;
  }

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <header className="bg-gradient-to-br from-[#E85D45] to-[#c14a36] text-white">
        <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl px-4 sm:px-6 py-5">
          <button onClick={() => navigate("/")} data-testid="back-home" className="flex items-center gap-2 mb-3 text-sm opacity-90">
            <ArrowRight className="w-4 h-4" />
            رجوع
          </button>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <FileText className="w-7 h-7" />
            المبلغ المتبقي (الدين)
          </h1>
          <p className="text-xs opacity-80 mt-1">تابع ديونك وادفع جزء منها بسهولة</p>
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl px-4 sm:px-6 py-6 space-y-5">
        {/* Total debt card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          data-testid="debt-total-card"
          className="bg-gradient-to-br from-[#FFE8E5] to-[#FFF5F3] border border-[#E85D45]/40 rounded-3xl p-6"
        >
          <AlertTriangle className="w-8 h-8 text-[#E85D45] mb-3" />
          <p className="text-sm text-[#5C6E64]">إجمالي ما عليك</p>
          <p data-testid="debt-amount" className="font-bold text-4xl text-[#E85D45]">
            {fmt(data?.total_debt)} <span className="text-xl">د.ع</span>
          </p>
          {(data?.total_debt || 0) > 0 && !hasPending && (
            <button
              onClick={() => setShowForm(true)}
              data-testid="open-pay-form"
              className="mt-4 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold px-6 py-3 rounded-full"
            >
              دفع مبلغ من الدين
            </button>
          )}
          {hasPending && (
            <div className="mt-3 bg-[#FFF8E1] border border-[#F5A623]/40 rounded-xl px-3 py-2 text-sm">
              <Clock className="w-4 h-4 inline ml-1 text-[#F5A623]" />
              لديك طلب سداد قيد المعالجة — سيتصل بك الموظف قريباً
            </div>
          )}
        </motion.div>

        {/* Pay form */}
        {showForm && (
          <motion.form
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            onSubmit={handlePay}
            className="bg-white border border-[#E4EBE6] rounded-2xl p-5 space-y-4"
          >
            <h3 className="font-bold text-[#111814]">دفع مبلغ من الدين</h3>
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">المبلغ الذي تريد دفعه *</label>
              <input
                type="number" step="0.01" min="1" max={data?.total_debt}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="pay-amount-input"
                placeholder={`الحد الأقصى: ${fmt(data?.total_debt)}`}
                required
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">ملاحظة (اختياري)</label>
              <textarea
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="مثل: وقت مناسب للاستلام"
                rows={2}
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setShowForm(false); setAmount(""); }} className="flex-1 px-6 py-3 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#2D4A3E] font-medium rounded-full">إلغاء</button>
              <button type="submit" disabled={submitting} data-testid="pay-submit-btn" className="flex-1 px-6 py-3 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold rounded-full disabled:opacity-50">
                {submitting ? "جاري الإرسال..." : "إرسال الطلب"}
              </button>
            </div>
          </motion.form>
        )}

        {/* Debt-causing orders */}
        {data?.debt_orders?.length > 0 && (
          <section data-testid="debt-orders">
            <h2 className="font-heading font-bold text-lg text-[#111814] mb-3">الطلبات التي خلّت دين</h2>
            <div className="space-y-2">
              {data.debt_orders.map((o) => (
                <div key={o.id} className="bg-white border border-[#E4EBE6] rounded-2xl p-3 flex justify-between items-center">
                  <div>
                    <p className="text-xs text-[#5C6E64]">{formatDate(o.delivered_at || o.created_at)}</p>
                    <p className="text-sm">طلب بقيمة {fmt(o.total)} — دُفع {fmt(o.amount_paid)}</p>
                  </div>
                  <span className="font-bold text-[#E85D45]">{fmt(o.amount_remaining)} د.ع</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Payment history */}
        {data?.payment_history?.length > 0 && (
          <section>
            <h2 className="font-heading font-bold text-lg text-[#111814] mb-3">سجل طلبات السداد</h2>
            <div className="space-y-2">
              {data.payment_history.map((p) => {
                const Icon = statusMap[p.status]?.icon || Clock;
                const color = statusMap[p.status]?.color || "#999";
                return (
                  <div key={p.id} data-testid={`pay-history-${p.id}`} className="bg-white border border-[#E4EBE6] rounded-2xl p-3 flex justify-between items-center">
                    <div>
                      <p className="font-bold text-[#111814]">{fmt(p.amount)} د.ع</p>
                      <p className="text-xs text-[#5C6E64]">{formatDate(p.created_at)}</p>
                    </div>
                    <span className="px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5" style={{ backgroundColor: `${color}20`, color }}>
                      <Icon className="w-3 h-3" />
                      {statusMap[p.status]?.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default Debts;
